import { CartUpdateEvent, ThemeEvents } from "@theme/events";

const DEFAULT_API_PATH = "/apps/pmecake/storefront/saved-baskets";
const initializedForms = new WeakSet();
const savingForms = new WeakSet();

/**
 * @typedef {{ product_id: number, variant_id: number, quantity: number }} CartItem
 * @typedef {{ items?: CartItem[] }} Cart
 */

/** @param {Cart | null | undefined} cart */
function buildItemsFromCart(cart) {
  return (cart?.items ?? []).map((item, index) => ({
    productId: `gid://shopify/Product/${item.product_id}`,
    variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
    quantity: item.quantity,
    position: index,
  }));
}

async function fetchCurrentCart() {
  const response = await fetch("/cart.js", {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new Error("Failed to load cart.");
  }
  return response.json();
}

/** @param {HTMLElement} root */
async function clearShopifyCart(root) {
  const sectionIds = [];
  document.querySelectorAll("cart-items-component").forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });

  const response = await fetch("/cart/clear.js", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify({
      sections: sectionIds.join(","),
      sections_url: window.location.pathname,
    }),
  });
  if (!response.ok) {
    throw new Error("Failed to clear cart.");
  }

  const clearedCart = await response.json();
  root.dispatchEvent(
    new CartUpdateEvent(clearedCart, "saved-basket-form", {
      itemCount: 0,
      source: "saved-basket-form",
      sections: clearedCart.sections,
    }),
  );
}

/**
 * @param {HTMLElement} root
 * @param {string} text
 * @param {"error" | "success"} tone
 */
function setMessage(root, text, tone) {
  const message = root.querySelector("[data-saved-basket-message]");
  if (!(message instanceof HTMLElement)) return;
  message.textContent = text;
  message.hidden = !text;
  message.classList.toggle("saved-basket-form__message--error", tone === "error");
  message.classList.toggle("saved-basket-form__message--success", tone === "success");
}

/** @param {HTMLElement} root */
async function saveBasket(root) {
  if (savingForms.has(root)) return;

  const inputElement = root.querySelector("[data-saved-basket-name]");
  const buttonElement = root.querySelector("[data-saved-basket-submit]");
  const input = inputElement instanceof HTMLInputElement ? inputElement : null;
  const button = buttonElement instanceof HTMLButtonElement ? buttonElement : null;
  const apiPath = root.dataset.apiPath || DEFAULT_API_PATH;
  const name = input?.value?.trim();

  if (!name) {
    setMessage(root, root.dataset.errorNameRequired || "Please enter a basket name.", "error");
    input?.focus();
    return;
  }

  savingForms.add(root);
  if (button) button.disabled = true;
  setMessage(root, "", "success");

  try {
    const cart = await fetchCurrentCart();
    const items = buildItemsFromCart(cart);

    if (!items.length) {
      setMessage(root, root.dataset.errorEmptyCart || "Your cart is empty.", "error");
      return;
    }

    const response = await fetch(apiPath, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "same-origin",
      body: JSON.stringify({ name, items }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || "Failed to save basket.");
    }

    if (input) input.value = "";
    setMessage(
      root,
      root.dataset.successMessage || "Basket saved successfully.",
      "success",
    );

    try {
      await clearShopifyCart(root);
    } catch (clearError) {
      console.error(clearError);
    }
  } catch (error) {
    setMessage(
      root,
      error instanceof Error ? error.message : "Failed to save basket.",
      "error",
    );
  } finally {
    savingForms.delete(root);
    if (button) button.disabled = false;
  }
}

/** @param {HTMLElement} root */
function initSavedBasketForm(root) {
  if (!root || initializedForms.has(root)) return;
  initializedForms.add(root);

  const form = root.querySelector(".saved-basket-form__form");
  if (!(form instanceof HTMLFormElement)) return;

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    saveBasket(root);
  });
}

function initAllSavedBasketForms() {
  document.querySelectorAll("[data-saved-basket-form]").forEach((root) => {
    if (root instanceof HTMLElement) {
      initSavedBasketForm(root);
    }
  });
}

function scheduleInitAllSavedBasketForms() {
  requestAnimationFrame(() => {
    initAllSavedBasketForms();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAllSavedBasketForms);
} else {
  initAllSavedBasketForms();
}

document.addEventListener("shopify:section:load", initAllSavedBasketForms);
document.addEventListener(ThemeEvents.cartUpdate, scheduleInitAllSavedBasketForms);
